import { useCallback, useEffect, useState } from "react";

import { api_without_cancellation } from "../../../api/api";
import { getCaseId, getClientId } from "../../../Utils/helper";

const createApiHook = (endpoint, getIdParam) => {
  return () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastFetched, setLastFetched] = useState(null);

    const idParam = getIdParam ? getIdParam() : null;
    const fullEndpoint = idParam ? `${endpoint}/${idParam}/` : endpoint;

    const fetchData = useCallback(
      async (force = false) => {
        if (force || !lastFetched) {
          try {
            setLoading(true);
            const response = await api_without_cancellation.get(fullEndpoint, {
              params: {
                client_id: getClientId(),
              },
            });
            setData(response.data);

            setLastFetched(new Date());
          } catch (error) {
            console.error(error);
            setError(error);
          } finally {
            setLoading(false);
          }
        }
      },
      [fullEndpoint, lastFetched]
    );

    useEffect(() => {
      const abortController = new AbortController();

      const initialFetch = async () => {
        try {
          setLoading(true);
          const response = await api_without_cancellation.get(fullEndpoint, {
            signal: abortController.signal,
            params: {
              client_id: getClientId(),
            },
          });
          setData(response.data);
          setLastFetched(new Date());
        } catch (error) {
          if (error.name !== "AbortError") {
            setError(error);
          }
        } finally {
          if (!abortController.signal.aborted) {
            setLoading(false);
          }
        }
      };

      initialFetch();

      return () => {
        abortController.abort();
      };
    }, [fullEndpoint]);

    return { data, loading, error, refetch: fetchData };
  };
};

export const useGetCaseProviders = createApiHook("/api/treatment/case-providers", getCaseId);
export const useGetTreatmentDates = createApiHook("/api/treatment/dates_treatment", getCaseId);
export const useGetTreatmentDetailsPageData = createApiHook(
  "/api/treatment-opt/treament_detail_page",
  getCaseId
);
export const useGetCaseSpecialities = createApiHook("/api/treatment/specialities", getCaseId);

export const useGetHipaaStatus = () => {
  const caseId = getCaseId();
  const [data, setData] = useState({
    has_generic_hipaa: null,
    template_configured: null,
  });
  const [loading, setLoading] = useState(Boolean(caseId));
  const [error, setError] = useState(null);

  const fetchHipaaStatus = useCallback(
    async (silent = false) => {
      if (!caseId) return null;

      if (!silent) setLoading(true);
      setError(null);
      try {
        const response = await api_without_cancellation.get(
          `/api/treatment-opt/hipaa-status/${caseId}/`
        );
        setData(response.data);
        return response.data;
      } catch (requestError) {
        setError(requestError);
        throw requestError;
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [caseId]
  );

  useEffect(() => {
    const abortController = new AbortController();

    if (caseId) {
      setLoading(true);
      setError(null);
      api_without_cancellation
        .get(`/api/treatment-opt/hipaa-status/${caseId}/`, {
          signal: abortController.signal,
        })
        .then((response) => setData(response.data))
        .catch((requestError) => {
          if (requestError.name !== "AbortError" && requestError.code !== "ERR_CANCELED") {
            setError(requestError);
          }
        })
        .finally(() => {
          if (!abortController.signal.aborted) setLoading(false);
        });
    }

    return () => abortController.abort();
  }, [caseId]);

  return { data, loading, error, refetch: fetchHipaaStatus };
};
